import React from 'react';
import translate from '@/utils/translate';
import pluralize from '@/utils/pluralize';
import type {Deliverable} from '../types';
import type {GigShellData} from '../shell/shellTypes';
import deliverableLabel from '../deliverableLabel';
import BriefSection from './BriefSection';
import styles from './BriefBlocks.module.scss';

// Kinds the legacy `required*Count` fields already cover. `deliverables[]` repeats
// them, so iterating both double-counts, as Ember's campaign-deliverables.hbs does.
const LEGACY_FIELD_KINDS = new Set([
  'instagram_post',
  'instagram_reel_post',
  'instagram_story_post',
]);

interface SummaryInput {
  counts: GigShellData['counts'];
  networkName: string | null;
  deliverables: Deliverable[];
}

interface Entry {
  key: string;
  count: number;
  label: string;
}

export function buildSummaryEntries({
  counts,
  networkName,
  deliverables,
}: SummaryInput): Entry[] {
  const network = networkName ?? 'Social';
  const entries: Entry[] = [];

  const uploads: Array<[string, number, string, string]> = [
    ['photo', counts.photo.required, 'Photo', 'Photos'],
    ['blog', counts.blog.required, 'Blog', 'Blogs'],
    ['video', counts.video.required, 'Video', 'Videos'],
    [
      'mixed-media',
      counts.mixedMedia.required,
      'Story Upload',
      'Story Uploads',
    ],
  ];
  uploads.forEach(([key, count, singular, plural]) => {
    if (count > 0) {
      entries.push({key, count, label: pluralize(count, singular, plural)});
    }
  });

  // Ember pluralizes each of these (campaign-deliverables.hbs).
  const posts: Array<[string, number, string, string]> = [
    [
      'feed-post',
      counts.feedPosts.required,
      '{network} Post',
      '{network} Posts',
    ],
    [
      'reels-post',
      counts.reels.required,
      '{network} Reels Post',
      '{network} Reels Posts',
    ],
    [
      'story-post',
      counts.stories.required,
      '{network} Story Post',
      '{network} Story Posts',
    ],
  ];
  posts.forEach(([key, count, singular, plural]) => {
    if (count > 0) {
      entries.push({
        key,
        count,
        label: translate(pluralize(count, singular, plural), {network}),
      });
    }
  });

  deliverables
    .filter((d) => !LEGACY_FIELD_KINDS.has(d.kind) && d.quantity > 0)
    .forEach((d) => {
      // Second arg is only a fallback for a null kind, which can't happen here.
      const label = deliverableLabel(d.kind, 'photo');
      entries.push({
        key: `deliverable-${d.kind}`,
        count: d.quantity,
        label: pluralize(d.quantity, label, `${label}s`),
      });
    });

  return entries;
}

function DeliverablesSummary({
  counts,
  networkName,
  deliverables,
}: SummaryInput) {
  const entries = buildSummaryEntries({counts, networkName, deliverables});
  if (entries.length === 0) {
    return null;
  }

  return (
    <BriefSection
      testID="brief-deliverables"
      title={translate('What deliverables am I producing?')}
    >
      <div className={styles.deliverables}>
        {entries.map(({key, count, label}) => (
          <div
            key={key}
            className={styles.deliverable}
            data-testid={`brief-deliverable-${key}`}
          >
            <span className={styles.deliverableCount}>{count}</span>{' '}
            <span className={styles.deliverableLabel}>{label}</span>
          </div>
        ))}
      </div>
    </BriefSection>
  );
}

export default DeliverablesSummary;
